import React, { memo, useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from '@beeline.com/bp-design-system';
import { toSearchParamsString } from 'utils/navigationUtil';
import { useHistory } from 'react-router';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';
import { GetViewHistoryDocument } from 'common/header/GlobalSearch/data/__generated__/getViewHistory.graphql';
import { GetSuggestedPersonsDocument } from 'common/header/GlobalSearch/data/__generated__/getSuggestedPersons.graphql';
import { GlobalSearchInput } from 'common/header/GlobalSearch/components/GlobalSearchInput';
import { SuggestionOption } from 'common/header/GlobalSearch/types';
import { suggestionOption, usePageSuggestionOptions } from 'common/header/GlobalSearch/data/suggestionOptions';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { useHeaderIntl } from '../translations/useHeaderIntl';

export const GlobalSearch = memo(() => {
  const { track } = useMixpanel();
  const history = useHistory();
  const intlHeaders = useHeaderIntl();
  const tenantCode = useTenantCode();
  const [recentlyViewedOptions, setRecentlyViewedOptions] = useState<SuggestionOption[]>([]);
  const [fetchedOptions, setFetchedOptions] = useState<SuggestionOption[]>([]);
  const options = useMemo(() => [...fetchedOptions, ...recentlyViewedOptions], [fetchedOptions, recentlyViewedOptions]);
  const { get: getSuggestedPersons, loading: loadingSuggestions } = useTypedQuery(GetSuggestedPersonsDocument);
  const getPageSuggestions = usePageSuggestionOptions();

  const { refetch: refetchRecentlyViewed, get: getRecentlyViewed } = useTypedQuery(GetViewHistoryDocument, {
    onCompleted: (data) => {
      if (data?.getViewHistory?.people?.length) {
        setRecentlyViewedOptions(
          suggestionOption.toRecentlyViewSuggestionOptions(data.getViewHistory.people, intlHeaders.recentlyViewed)
        );
      }
    },
    fetchPolicy: FETCH_POLICY.NO_CACHE
  });

  useEffectOnce(() => {
    getRecentlyViewed({
      variables: {
        size: 3
      }
    });
  });

  const handleSearch = useCallback(
    async (search: string) => {
      if (!search) {
        setFetchedOptions([]);
        return;
      }
      const { data: people } = await getSuggestedPersons({
        variables: {
          search,
          limit: 5
        }
      });

      setFetchedOptions([
        suggestionOption.toWorkerHubSearchOption(search, intlHeaders.searchInWorkerHub({ search })),
        ...suggestionOption.toPersonSuggestionOptions(people?.suggest.suggestions),
        ...getPageSuggestions(search)
      ]);
    },
    [getSuggestedPersons, intlHeaders, getPageSuggestions]
  );

  const handleChange = useCallback(
    (value: SuggestionOption['value']) => {
      track(value.event);
      if ('url' in value) {
        history.push(`/${tenantCode}/${value.url}`);
        return;
      }
      if ('personUid' in value) {
        history.push(`/${tenantCode}/profile/${value.personUid}`);
        return;
      }
      if (value.workerHubSearch) {
        const goToHub = () => {
          history.push(`/${tenantCode}/workers?${toSearchParamsString({ query: { search: value.workerHubSearch } })}`);
        };
        if (history?.location?.pathname.includes(`/${tenantCode}/workers`)) {
          // workaround if someone use global search in workers table
          // worker table will not handle this case so we need to first, take him somewhere else (params updates are controlled only in DataTable component)
          history.push(`/${tenantCode}`);
          setTimeout(() => {
            goToHub();
          }, 50);
          return;
        }
        goToHub();
      }
    },
    [history, tenantCode, track]
  );

  const handleOnOpen = useCallback(() => {
    track(MixpanelEvents.general.APP_HEADER_SEARCH_OPEN);
  }, [track]);

  const handleClose = useCallback(() => {
    track(MixpanelEvents.general.APP_HEADER_SEARCH_CLOSE);
    setTimeout(() => {
      refetchRecentlyViewed();
    }, 300);
  }, [refetchRecentlyViewed, track]);

  return (
    <GlobalSearchInput
      onOpen={handleOnOpen}
      onSearch={handleSearch}
      onChange={handleChange}
      onClose={handleClose}
      options={options}
      loading={loadingSuggestions}
    />
  );
});
